import { teamMemberColumns } from "@asap/shared/utils/supabase.columns";
import type { TeamMemberSelectionEntry } from "@asap/shared/schemas/team-member-select";
import type { TeamMember, TeamMemberForAvatar } from "@asap/shared/utils/supabase.types";
import { createSharedComposable, useLocalStorage } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import { useToast } from "~/composables/useToast";
import { useUserMe } from "~/components/user/composables/useUser";
import { useAsapWorkspace } from "~/components/workspace/composables/useWorkspace";

export const useManagees = createSharedComposable(() => {
  const { teamMember } = useUserMe();
  const supabase = useSupabase();
  const { invoke } = useApi();
  const { addToastError } = useToast();
  const { asapWorkspaceId } = useAsapWorkspace();
  const managees = ref<TeamMember[]>([]);
  const directManagees = ref<TeamMember[]>([]);
  const consultantFilterPreference = useLocalStorage<string>("settings_consultant_filter", "me_and_managees");
  const selectedConsultantsPreference = useLocalStorage<TeamMemberSelectionEntry[]>(
    "settings_consultant_selection",
    []
  );

  /**
   * Fetch all managees of the current user N-1 or N-n
   * @returns {Promise<TeamMember[]>}
   */
  const fetchManagees = async (): Promise<TeamMember[]> => {
    try {
      const { data: manageesIdsArray, error: manageesError } = await supabase.rpc("get_current_user_managees");
      if (manageesError) throw new Error(manageesError.message);
      if (!manageesIdsArray.length) return [];

      const manageesIds = manageesIdsArray.map((el) => el.managee_id);
      const { data: managedTeamMemberData, error: managedTeamMemberError } = await supabase
        .from("team_member")
        .select<string, TeamMember>(teamMemberColumns)
        .in("id", manageesIds)
        .is("offboarded_at", null);

      if (managedTeamMemberError) throw new Error(managedTeamMemberError.message);

      return managedTeamMemberData;
    } catch (error) {
      addToastError(
        {
          title: "Une erreur s'est produit lors du chargement des personnes dont vous êtes le manager ou le référent",
        },
        error
      );
      throw error;
    }
  };

  /**
   * Fetch all direct managees of the current user (only N-1)
   * @param {string} teamMemberId - The ID of the team member to fetch direct managees for
   * @returns {Promise<TeamMember[]>}
   */
  const fetchDirectManagees = async (teamMemberId: string): Promise<TeamMember[]> => {
    try {
      const { data, error } = await supabase
        .from("team_member")
        .select<string, TeamMember>(teamMemberColumns)
        .eq("manager_id", teamMemberId)
        .is("offboarded_at", null);

      if (error) throw new Error(error.message);
      return data;
    } catch (error) {
      addToastError(
        {
          title: "Une erreur s'est produit lors du chargement des personnes dont vous êtes le manager ou le référent",
        },
        error
      );
      throw error;
    }
  };

  // Shared by every caller so one page render fetches the managees once, even with zero managees.
  let manageesLoad: { teamMemberId: string; promise: Promise<void> } | undefined;

  const loadManagees = (teamMemberId: string): Promise<void> => {
    if (manageesLoad?.teamMemberId === teamMemberId) return manageesLoad.promise;

    const promise = (async () => {
      managees.value = await fetchManagees();
      directManagees.value = await fetchDirectManagees(teamMemberId);
    })();
    promise.catch(() => {
      if (manageesLoad?.promise === promise) manageesLoad = undefined;
    });
    manageesLoad = { teamMemberId, promise };
    return promise;
  };

  watch(
    () => teamMember.value?.id,
    (teamMemberId) => {
      if (teamMemberId) void loadManagees(teamMemberId);
    },
    { immediate: true }
  );

  const areSomeManageesIndependent = computed<boolean>(
    () => managees.value?.some((managee) => managee.main_workspace_id !== asapWorkspaceId.value) ?? false
  );

  const isManager = computed<boolean>(() => managees.value.length > 0);

  /**
   * Check if a potential managee is one of the current user's managees (N-1 or N-n)
   * @param {string | undefined | null} potentialManageeId - The ID of the potential managee to check
   * @returns {boolean}
   */
  const isMyManagee = function (potentialManageeId: string | undefined | null) {
    if (!potentialManageeId) return false;
    if (!managees.value.length) return false;
    return managees.value.some((managee) => managee.id === potentialManageeId);
  };

  /**
   * Check if a potential managee is one of the current user's direct managees (N-1)
   * @param {string | undefined | null} potentialManageeId - The ID of the potential managee to check
   * @returns {boolean}
   */
  const isMyDirectManagee = function (potentialManageeId: string | undefined | null) {
    if (!potentialManageeId) return false;
    if (!directManagees.value.length) return false;
    return directManagees.value.some((managee) => managee.id === potentialManageeId);
  };

  const managementLevel = computed(() => {
    if (!teamMember.value) return;
    return teamMember.value.management_level;
  });

  const isCountryManager = computed(() => {
    if (!managementLevel.value) return false;
    return managementLevel.value >= 3;
  });

  const updateManagementLevel = async (
    teamMemberId: string,
    actualManagementLevel: number,
    direction: "increase" | "decrease"
  ) => {
    const adjustment = direction === "increase" ? 1 : -1;
    const newLevel = actualManagementLevel + adjustment;

    if (newLevel < 1 || newLevel > 4) {
      throw new Error(
        `Impossible de ${direction === "increase" ? "monter au-dessus de 4" : "descendre en dessous de 1"}`
      );
    }

    const { error } = await supabase.from("team_member").update({ management_level: newLevel }).eq("id", teamMemberId);

    if (error) throw new Error(error.message);
  };

  const ensureManageesLoaded = async () => {
    if (!teamMember.value) return;
    await loadManagees(teamMember.value.id);
  };

  const getMeAndManageeConsultants = async (): Promise<TeamMember[]> => {
    if (!teamMember.value?.id) return [];
    await ensureManageesLoaded();
    const manageeTeamMembers = managees.value.filter(
      (managee) => managee.auth_user?.user_role.find((role) => role.role === "consultant_team_member") !== undefined
    );
    const consultantTeamMembers =
      teamMember.value.auth_user?.user_role.find((role) => role.role === "consultant_team_member") !== undefined
        ? [teamMember.value]
        : [];
    return [...consultantTeamMembers, ...manageeTeamMembers];
  };

  const getMeAndManageeConsultantIds = async (): Promise<string[]> => {
    const consultantTeamMembers = await getMeAndManageeConsultants();
    return consultantTeamMembers.map((consultantTeamMember) => consultantTeamMember.id);
  };

  const getSelectedConsultantIds = (): string[] =>
    selectedConsultantsPreference.value
      .filter((entry): entry is { type: "id"; value: string } => entry.type === "id")
      .map((entry) => entry.value);

  /** The "selection" preference as entries, for the filters built on TeamMemberSelectV2. */
  const getSelectedConsultantEntriesFromSettingFilter = (): TeamMemberSelectionEntry[] | undefined => {
    if (consultantFilterPreference.value !== "selection") return undefined;
    if (selectedConsultantsPreference.value.length === 0) return undefined;
    return selectedConsultantsPreference.value;
  };

  /** The consultants of the "selection" preference, for the filters that hold full team members. */
  const getSelectedConsultantsFromSettingFilter = async (): Promise<TeamMemberForAvatar[] | undefined> => {
    if (consultantFilterPreference.value !== "selection") return undefined;
    const selectedConsultantIds = getSelectedConsultantIds();
    if (selectedConsultantIds.length === 0) return undefined;

    const response = await invoke({
      path: "team-member",
      method: "GET",
      query: { ids: selectedConsultantIds },
    });
    if (!response.success) {
      addToastError({ title: "Impossible de charger les consultants de ton filtre par défaut" }, response.error);
      return undefined;
    }
    return response.data;
  };

  const getConsultantIdsFromSettingFilter = async (): Promise<string[]> => {
    if (consultantFilterPreference.value === "selection") return getSelectedConsultantIds();
    if (consultantFilterPreference.value === "me_and_managees") return getMeAndManageeConsultantIds();
    return [];
  };

  return {
    managees,
    directManagees,
    isManager,
    isCountryManager,
    areSomeManageesIndependent,
    managementLevel,
    isMyManagee,
    isMyDirectManagee,
    updateManagementLevel,
    ensureManageesLoaded,
    consultantFilterPreference,
    getMeAndManageeConsultantIds,
    getConsultantIdsFromSettingFilter,
    getSelectedConsultantsFromSettingFilter,
    getSelectedConsultantEntriesFromSettingFilter,
  };
});
